/* eslint-disable */

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle2 } from 'lucide-react'
import PostHogTracker from '../../utils/PostHogTracker'
import { POSTHOG_EVENTS, POSTHOG_PROPERTIES } from '../../constants/posthogEvents'
import { getUserDetails } from '../../utils/AuthUtil'
import {
  DEFAULT_PRIMARY_RM,
  DEFAULT_SERVICE_RM,
  fetchCustomerRelationshipDetails,
  openWhatsAppChat,
} from '../../utils/relationshipManager'

import ajathImg from '../../assets/RM/ajath.png'
import jeevaImg from '../../assets/RM/jeeva.png'
import adheipImg from '../../assets/RM/adheip.png'
import pasupathiImg from '../../assets/RM/pasupathi.png'
import fabitsImg from '../../assets/RM/fabits.png'
import { AnimatedStateButton } from '../ui/animated-state-button'

const RM_IMAGES = [
  { match: 'ajath', src: ajathImg },
  { match: 'jeeva', src: jeevaImg },
  { match: 'jeeav', src: jeevaImg },
  { match: 'adheip', src: adheipImg },
  { match: 'pasupathi', src: pasupathiImg },
]

const getRmImageByName = (name = '') => {
  const normalized = name.toLowerCase()
  const found = RM_IMAGES.find(({ match }) => normalized.includes(match))
  return found?.src || fabitsImg
}

export const isRmOnline = (date = new Date()) => {
  const day = date.getDay()
  const minutes = date.getHours() * 60 + date.getMinutes()

  return day >= 1 && day <= 5 && minutes >= 9 * 60 && minutes < 18 * 60
}

// Component 1: RMMessageButton
const RMMessageButton = ({ onContact }) => {
  const handleClick = (e) => {
    e.stopPropagation()
    onContact()
  }

  return (
    <AnimatedStateButton
      state='idle'
      states={{
        idle: {
          label: 'Send Message',
          className: 'bg-primary-600 hover:bg-primary-700 text-white',
        },
      }}
      onClick={handleClick}
      style={{ boxShadow: '0 8px 24px rgba(40, 24, 255, 0.18)' }}
      className='relative h-11 min-h-11 w-full min-w-0 shrink-0 cursor-pointer select-none overflow-hidden rounded-full font-IBM text-sm font-semibold leading-5 text-white transition-all duration-300 min-[360px]:flex-1'
    />
  )
}

// Component 2: RMVoiceCallButton
const RMVoiceCallButton = ({ rm, onContact, onOfflineClick, isOnline }) => {
  const handleClick = (e) => {
    e.stopPropagation()

    if (!isOnline) {
      onOfflineClick()
      return
    }

    onContact()

    const phone = (rm.phone || '').replace(/\D/g, '')
    if (phone) window.location.href = `tel:${phone}`
  }

  return (
    <AnimatedStateButton
      state='idle'
      states={{
        idle: {
          label: 'Call Advisor',
        },
      }}
      onClick={handleClick}
      style={{
        background: isOnline ? 'var(--fb-surface)' : 'var(--fb-surface-muted)',
        border: '1.5px solid var(--fb-border-strong)',
      }}
      className={`relative h-11 min-h-11 w-full min-w-0 shrink-0 cursor-pointer select-none overflow-hidden rounded-full font-IBM text-sm font-semibold leading-5 transition-all duration-300 min-[360px]:flex-1 ${
        isOnline
          ? 'text-[var(--fb-text-strong)] hover:bg-[var(--fb-surface-muted)]'
          : 'text-black-500 opacity-60'
      }`}
    />
  )
}

const mapManagerToCard = (apiManager, fallback) => {
  if (!apiManager?.managerFullName && !apiManager?.managerPhoneNumber) {
    return {
      ...fallback,
      s3_headshot_url: getRmImageByName(fallback.name),
    }
  }

  const name = (apiManager.managerFullName || fallback.name).trim()

  return {
    ...fallback,
    name,
    email: apiManager.managerEmail || fallback.email,
    phone: apiManager.managerPhoneNumber || fallback.phone,
    s3_headshot_url: getRmImageByName(name),
  }
}

const DedicatedWealthTeam = () => {
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' })
  const [customerDetails, setCustomerDetails] = useState(null)
  const [isOnline, setIsOnline] = useState(() => isRmOnline())

  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(isRmOnline())
    const intervalId = setInterval(updateOnlineStatus, 60 * 1000)

    return () => clearInterval(intervalId)
  }, [])

  const fetchCustomerKycDetails = async (clientCode) => {
    return fetchCustomerRelationshipDetails(clientCode)
  }

  useEffect(() => {
    const loadCustomerDetails = async () => {
      const userDetails = getUserDetails()
      const clientCode = userDetails?.uid?.split('-')[0]?.toUpperCase()

      if (!clientCode) return

      const details = await fetchCustomerKycDetails(clientCode)
      if (details) {
        setCustomerDetails(details)
      }
    }

    loadCustomerDetails()
  }, [])

  const hasApiPrimaryRm = !!(
    customerDetails?.rm?.managerFullName || customerDetails?.rm?.managerPhoneNumber
  )
  const hasApiServiceRm = !!(
    customerDetails?.serviceRm?.managerFullName || customerDetails?.serviceRm?.managerPhoneNumber
  )
  const hasApiTeam = hasApiPrimaryRm || hasApiServiceRm

  const teamCards = hasApiTeam
    ? [
        ...(hasApiPrimaryRm
          ? [['primaryRM', mapManagerToCard(customerDetails.rm, DEFAULT_PRIMARY_RM)]]
          : []),
        ...(hasApiServiceRm
          ? [['serviceRM', mapManagerToCard(customerDetails.serviceRm, DEFAULT_SERVICE_RM)]]
          : []),
      ]
    : [['primaryRM', mapManagerToCard(null, DEFAULT_PRIMARY_RM)]]

  const showToastNotification = (message, type = 'success') => {
    setToast({ show: true, message, type })
    setTimeout(() => {
      setToast((prev) => ({ ...prev, show: false }))
    }, 4000)
  }

  const showOfflineAdvisorMessage = () => {
    showToastNotification('Advisor offline. Send message instead.', 'info')
  }

  const executeContactLog = async (rmDetails, actionType) => {
    const phone = (rmDetails.phone || '').replace(/\D/g, '')
    const eventProps = {
      [POSTHOG_PROPERTIES.ADVISOR_NAME]: rmDetails.name,
      [POSTHOG_PROPERTIES.ADVISOR_TYPE]: rmDetails.type,
    }

    if (actionType === 'MESSAGE') {
      PostHogTracker.track(POSTHOG_EVENTS.SEND_MESSAGE_CLICKED, eventProps)

      if (phone) {
        openWhatsAppChat(phone)
        showToastNotification(`Opening WhatsApp with ${rmDetails.name}.`, 'success')
      }
      return
    }

    if (actionType === 'VOICE_CALL') {
      PostHogTracker.track(POSTHOG_EVENTS.CALL_ADVISOR_CLICKED, eventProps)
      showToastNotification(`Connecting you with ${rmDetails.name}.`, 'success')
    }
  }

  const renderCard = (key, rm) => {
    return (
      <motion.div
        key={key}
        className='fabits-card flex-1 flex flex-col justify-between relative overflow-visible p-4 transition-all duration-300'
        whileHover={{ y: -3, transition: { duration: 0.2 } }}
      >
        <div className='flex flex-row items-start gap-3 sm:gap-4 mb-3.5'>
          {/* Avatar Area with dynamic image rendering */}
          <div className='relative shrink-0'>
            <div className='h-12 w-12 overflow-hidden rounded-full border border-black-200 bg-black-100'>
              <img
                src={rm.s3_headshot_url}
                alt={rm.name}
                className='h-full w-full object-cover'
                onError={(e) => {
                  e.target.onerror = null
                  e.target.src = fabitsImg
                }}
              />
            </div>
            {/* Availability Dot */}
            <div
              className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white ${
                isOnline ? 'bg-secondary-500' : 'bg-black-400'
              }`}
            />
          </div>

          {/* Details & Roles */}
          <div className='flex-1 min-w-0 text-left space-y-1'>
            <div className='flex items-center flex-wrap gap-2'>
              <h3 className='font-IBM font-semibold text-black-950 text-sm lg:text-base'>
                {rm.name}
              </h3>
              <span className='fabits-role-chip inline-flex items-center rounded bg-primary-50 px-2 py-0.5 text-xs font-semibold text-primary-600 border border-primary-100'>
                {key === 'primaryRM' ? 'Strategy' : 'Operations'}
              </span>
            </div>
            <p className='fabits-eyebrow'>{rm.role}</p>
            <p className='text-xs text-black-600 font-normal pr-2 leading-relaxed'>
              {rm.roleDescription}
            </p>
          </div>
        </div>

        <div className='shrink-0'>
          {/* Separator */}
          <hr className='my-3 border-black-200' />

          {/* Interactive Action Buttons */}
          <div className='flex w-full shrink-0 flex-col gap-2.5 min-[360px]:flex-row'>
            <RMVoiceCallButton
              rm={rm}
              isOnline={isOnline}
              onOfflineClick={showOfflineAdvisorMessage}
              onContact={() => executeContactLog(rm, 'VOICE_CALL')}
            />
            <RMMessageButton rm={rm} onContact={() => executeContactLog(rm, 'MESSAGE')} />
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <div className='w-full lg:max-w-[1128px] mx-auto mt-6'>
      {/* Toast Notification */}
      <AnimatePresence>
        {toast.show && (
          <div className='fixed bottom-6 inset-x-0 z-50 flex justify-center pointer-events-none px-4'>
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className='pointer-events-auto flex items-center gap-3 rounded-lg bg-black-950 px-5 py-4 text-white shadow-xl max-w-sm w-full sm:w-auto'
            >
              <CheckCircle2 className='h-5 w-5 text-secondary-300 shrink-0' />
              <p className='text-sm font-medium'>{toast.message}</p>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className='mb-4 flex items-center justify-between'>
        <div>
          <h2 className='font-IBM text-xl font-bold leading-tight text-black-950'>
            Your Dedicated Team
          </h2>
          <p className='text-sm text-black-500 font-normal mt-1'>
            Get in touch with your wealth managers.
          </p>
        </div>
        <div
          className={`hidden sm:flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
            isOnline
              ? 'fabits-online-pill border-secondary-200 bg-secondary-50 text-secondary-900'
              : 'border-black-300 bg-black-200 text-black-600'
          }`}
        >
          <span className='relative flex h-2 w-2'>
            {isOnline && (
              <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary-300 opacity-75'></span>
            )}
            <span
              className={`relative inline-flex h-2 w-2 rounded-full ${
                isOnline ? 'bg-secondary-500' : 'bg-black-500'
              }`}
            ></span>
          </span>
          {isOnline ? 'Online' : 'Offline'}
        </div>
      </div>

      <div className='flex flex-col md:flex-row gap-6'>
        {teamCards.map(([key, rm]) => renderCard(key, rm))}
      </div>
    </div>
  )
}

export default DedicatedWealthTeam
